import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { CartLine, SalesLine, SimpleProduct, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ICartlinesViewProps } from '../../../modules/ext-cart/components/ext-cart-line-items';
import { formatPrice } from '../../../utils/helper';
import { RelewiseLineItem, RelewiseCart, RelewiseOrder } from '../types/tracking';
import {
    MappedProduct,
    Media,
    PrimarySizeVariant,
    ProductColor,
    Label,
    ProductMetaInfo,
    SearchSuggestions,
    VariantDetails,
    MemberPriceData
} from '../types/mapped';
import {
    ProductData,
    RelewiseDataObject,
    RelewiseLabel,
    RelewiseMedia,
    RelewiseProductResult,
    RelewiseVariantResult,
    RelewiseCategoryResult,
    VariantData,
    AutosuggestSearchResult
} from '../types/relewiseData';
import { BaseRelewiseService, InitializedRelewiseService } from './BaseRelewiseService/BaseRelewiseService';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { TranslationKey } from '../../../shared/functions/get-translation';
import { MEMBER_PRICE_LABEL_FALLBACK_TEXT } from '../const';

interface VariantDimensions {
    color?: string;
    size?: string;
}

const DIMENSION_TYPE_VALUES_MAP: Record<number, keyof VariantDimensions | undefined> = {
    1: 'color',
    3: 'size'
};
const PRODUCT_LABELS_DATA_KEY_PREFIX = 'ProductLabels';

interface LineItemInput {
    product: SimpleProduct | undefined;
    lineData: CartLine | SalesLine | undefined;
}

interface InitializedService extends InitializedRelewiseService {
    sitePath: string;
    baseUrl: string;
}

export class RelewiseMappingService extends BaseRelewiseService<InitializedService> {
    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Mapping Service');
    }

    public prefixId(itemId: string): string;
    public prefixId(itemId: string | undefined): string | undefined;
    public prefixId(itemId: string | undefined): string | undefined {
        if (!this.isEnabled() || !itemId) {
            return;
        }
        const envName = this.environmentName;

        return `${envName}|${itemId}`;
    }

    public unprefixId(prefixedId: string): string;
    public unprefixId(prefixedId: string | undefined): string | undefined;
    public unprefixId(prefixedId: string | undefined): string | undefined {
        if (!prefixedId) {
            return;
        }
        const parts = prefixedId.split('|');
        return !!parts.length ? (parts.pop() ?? '') : prefixedId;
    }

    public mapProductDimensionsToRelewiseVariantId(itemId: string, color: string, size: string) {
        const normalizedColor = color.replace(/ /g, '_').replace(/#/g, '');
        const normalizedSize = size.replace(/ /g, '_').replace(/\//g, '');

        return `${this.prefixId(itemId)}${normalizedColor}${normalizedSize}`;
    }

    public mapRelewiseProductToProductMetaInfo(productCustomData: ProductData | undefined): ProductMetaInfo | undefined {
        if (!this.isEnabled() || !productCustomData) {
            return;
        }
        return {
            description: productCustomData.Description?.value ?? '',
            friendlyDescription: productCustomData.FriendlyDescription?.value ?? '',
            composition: productCustomData.Composition?.value ?? '',
            careInstructions: {
                bleaching: productCustomData.CareBleaching?.value ?? '',
                dryCleaning: productCustomData.CareDryCleaning?.value ?? '',
                ironing: productCustomData.CareIroning?.value ?? '',
                naturalDrying: productCustomData.CareNaturalDrying?.value ?? '',
                tumbleDrying: productCustomData.CareTumbleDrying?.value ?? '',
                washing: productCustomData.CareWashing?.value ?? ''
            }
        };
    }

    public mapToRelewiseCart(subtotalAmount: number, cartLines: ICartlinesViewProps[] | undefined): RelewiseCart {
        const lineItems =
            cartLines?.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.cartline,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: subtotalAmount, currency: this.currencyId ?? '' },
            lineItems
        };
    }

    public mapToRelewiseOrder(salesLines: ISalesLine[], salesOrder: SalesOrder): RelewiseOrder {
        const lineItems =
            salesLines.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.salesLine,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: salesOrder.SubtotalAmount ?? 0, currency: this.currencyId ?? '' },
            lineItems,
            orderNumber: salesOrder.Id
        };
    }

    private mapToRelewiseLineItem({ lineData, product }: LineItemInput): RelewiseLineItem {
        const variantId = this.mapSimpleProductToRelewiseVariantId(product);
        return {
            lineTotal: lineData?.TotalAmount ?? 0,
            productId: this.prefixId(lineData?.ItemId) ?? '',
            quantity: lineData?.Quantity ?? 0,
            ...(variantId ? { variantId } : {})
        };
    }

    public mapSimpleProductToRelewiseVariantId(product: SimpleProduct | undefined): string | undefined {
        if (!product?.ItemId) {
            return;
        }

        const variantDimensions = product.Dimensions?.reduce<VariantDimensions>((acc, val) => {
            const dimensionName = DIMENSION_TYPE_VALUES_MAP[val.DimensionTypeValue];
            if (dimensionName) {
                acc[dimensionName] = val.DimensionValue?.Value;
            }
            return acc;
        }, {});

        const { color, size } = variantDimensions ?? {};
        if (!color || !size) {
            return;
        }

        return this.mapProductDimensionsToRelewiseVariantId(product.ItemId, color, size);
    }

    public mapRelewiseProductResultList(products: RelewiseProductResult[] | undefined): MappedProduct[] {
        if (!products) {
            return [];
        }
        return products.map(p => this.mapRelewiseProductResult(p)).filter((p): p is MappedProduct => !!p);
    }

    public mapRelewiseProductResult(product: RelewiseProductResult): MappedProduct | undefined {
        if (!this.isEnabled()) {
            return;
        }
        const { basePriceFormatted, finalPriceFormatted } = this.formatVariantPrices(product.variant);
        const discount = this.getVariantDiscount(product.variant);

        const variant = product.variant;
        const variantCustomData = product.variant?.data;
        const productCustomData = product.data;
        const productPageUrl = `${this.sitePath}${variantCustomData?.RelativeUrl?.value ?? ''}`;

        const mappedLabels = this.extractLabels(variant);

        const memberPriceLabel = mappedLabels?.find(label => label.type === 'MemberDiscount');

        const formattedMemberPrice = this.getFormattedMemberPrice(variant);
        const memberPriceTag: MemberPriceData | undefined =
            formattedMemberPrice && memberPriceLabel
                ? {
                      text: formattedMemberPrice,
                      color: memberPriceLabel?.color,
                      textColor: memberPriceLabel?.textColor
                  }
                : undefined;

        const primaryImage = variantCustomData?.Media?.value?.$values.find(media => media.data?.IsPrimary?.value)?.data;
        const primaryImageUrl = primaryImage?.Url?.value ?? '';

        return {
            relewiseVariantId: variant?.variantId ?? undefined,
            variantRecordId: variantCustomData?.RecordId?.value,
            concatenatedId: this.unprefixId(variant?.variantId ?? undefined),
            productRecordId: productCustomData?.RecordId?.value,
            productItemId: this.unprefixId(product.productId ?? undefined),
            relewiseProductId: product.productId ?? undefined,
            // Use master product display name in Relewise features
            displayName: product?.displayName ?? '',
            imgUrl: primaryImageUrl,
            basePrice: variant?.listPrice ?? undefined,
            finalPrice: variant?.salesPrice ?? undefined,
            basePriceFormatted,
            finalPriceFormatted,
            hasDiscount: !!discount && !formattedMemberPrice,
            discount,
            productPageUrl,
            productColors: this.mapProductColors(product.data),
            color: this.mapVariantColor(variant),
            labels: mappedLabels,
            brand: product.brand?.displayName ?? undefined,
            priceCurrency: this.currency ?? null,
            isInStock: true,
            suggestedGender: null,
            primarySizeVariant: this.mapPrimarySizeVariant(variantCustomData),
            memberPriceData: memberPriceTag
        };
    }

    public mapRelewiseVariantToVariantDetails(
        productData: RelewiseProductResult,
        variant: RelewiseVariantResult
    ): VariantDetails | undefined {
        const variantCustomData = variant?.data;
        if (!this.isEnabled() || !variantCustomData?.RecordId.value) {
            return;
        }

        const sortedPdpLabels = variantCustomData?.[`ProductLabels_${this.legalEntityId}`]?.value?.$values
            ?.slice()
            .filter(relewiseLabel => relewiseLabel.data?.Type?.value === 'Custom' || relewiseLabel.data?.Type?.value === 'MemberDiscount')
            .sort((a, b) => {
                const orderA = a.data?.Order?.value ?? Number.POSITIVE_INFINITY;
                const orderB = b.data?.Order?.value ?? Number.POSITIVE_INFINITY;
                return orderA - orderB;
            });

        const memberPriceLabel = sortedPdpLabels?.find(label => label.data?.Type?.value === 'MemberDiscount');
        const formattedMemberPrice = this.getFormattedMemberPrice(variant, true);
        const hasMemberPriceData = memberPriceLabel && formattedMemberPrice;

        const inlineLabel = hasMemberPriceData
            ? memberPriceLabel
            : sortedPdpLabels?.find(label => label.data?.Position?.value === 'inline' && label.data?.Type?.value !== 'MemberDiscount');
        const badgeLabel = sortedPdpLabels?.find(label => label.data?.Position?.value === 'badge');
        const mappedInlineLabel = this.mapLabel(inlineLabel);
        const mappedBadgeLabel = this.mapLabel(badgeLabel);
        const finalInlineLabel: Label | undefined =
            hasMemberPriceData && mappedInlineLabel ? { ...mappedInlineLabel, text: formattedMemberPrice } : mappedInlineLabel;

        const { basePriceFormatted, finalPriceFormatted } = this.formatVariantPrices(variant) ?? {};
        const discount = this.getVariantDiscount(variant);
        const media = this.buildVariantMediaList(productData, variant);

        return {
            relewiseVariantId: variant.variantId ?? '',
            concatenatedId: this.unprefixId(variant.variantId ?? undefined),
            variantRecordId: variantCustomData.RecordId.value,
            // Use master product display name in Relewise features
            displayName: productData.displayName ?? '',
            primaryImageUrl: media.find(m => m.isPrimary)?.url ?? '',
            media,
            basePrice: variant.listPrice ?? undefined,
            variantFullUrl: `${this.baseUrl}${this.sitePath}${variantCustomData.RelativeUrl.value ?? ''}`,
            finalPrice: variant.salesPrice ?? undefined,
            basePriceFormatted,
            finalPriceFormatted,
            hasDiscount: !!discount && !formattedMemberPrice,
            discount,
            primarySizeVariant: this.mapPrimarySizeVariant(variantCustomData),
            fallbackStockCount: variantCustomData[`StockCount_${this.legalEntityId}`]?.value,
            isInAssortment: variantCustomData[`InAssortment_${this.legalEntityId}`]?.value ?? false,
            color: this.mapVariantColor(variant),
            size: variantCustomData.Size?.value ?? '',
            inlineLabel: finalInlineLabel,
            badgeLabel: mappedBadgeLabel,
            hasMemberPrice: !!formattedMemberPrice
        };
    }

    private mapMediaCollection(mediaObjects: RelewiseDataObject<RelewiseMedia>[] | undefined): Media[] {
        return (mediaObjects ?? [])
            .map(mediaObject => mediaObject.data)
            .filter(media => !!media?.Url?.value)
            .map(media => ({
                altText: media.AltText?.value ?? '',
                url: media.Url.value ?? '',
                isPrimary: media.IsPrimary?.value ?? false,
                displayOrder: media.DisplayOrder?.value ?? Number.POSITIVE_INFINITY,
                type: media.Type?.value ?? 'Image',
                thumbnail: media.Thumbnail?.value
            }))
            .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.displayOrder - b.displayOrder);
    }

    /**
     * Builds the full media list to display for a variant:
     * variant media first (primary leading), then master product media, deduplicated by url.
     */
    private buildVariantMediaList(productData: RelewiseProductResult, variant: RelewiseVariantResult): Media[] {
        const variantMedia = this.mapMediaCollection(variant.data?.Media?.value?.$values);
        const productMedia = this.mapMediaCollection(productData.data?.Media?.value?.$values);

        const seenUrls = new Set<string>();
        return [...variantMedia, ...productMedia].filter(media => {
            if (seenUrls.has(media.url)) {
                return false;
            }
            seenUrls.add(media.url);
            return true;
        });
    }

    public mapAutosuggestedSearchResult(result: AutosuggestSearchResult | undefined): SearchSuggestions | undefined {
        if (!this.isEnabled() || !result) {
            return;
        }

        return {
            categories: result.categories?.map(category => ({
                id: category.categoryId ?? '',
                label: category.displayName ?? '',
                url: this.buildCategoryPlpUrl(category) ?? ''
            })),
            keywords: result.searchTermPredictions?.map(prediction => ({
                id: prediction.term ?? '',
                label: prediction.term ?? '',
                url: `${this.sitePath}/search?q=${encodeURIComponent(prediction.term ?? '')}`
            })),
            products: this.mapRelewiseProductResultList(result.products)
        };
    }

    public mapProductColors(data: ProductData | undefined): ProductColor[] | undefined {
        if (!data) {
            return undefined;
        }

        return data?.[`Colors_${this.legalEntityId}`]?.value?.$values
            ?.map(c => ({ name: c.data.Name.value, hex: c.data.Hex.value }))
            .filter((c): c is ProductColor => !!c.name && !!c.hex);
    }

    private mapVariantColor(variant: RelewiseVariantResult | undefined): ProductColor {
        return {
            name: variant?.data?.Color?.value ?? '',
            hex: variant?.data?.ColorHexCode?.value ?? '',
            colorRefinerGroup: variant?.data?.ColorRefinerGroup?.value
        };
    }

    public extractLabels(variant: RelewiseVariantResult | undefined): Label[] {
        if (!variant?.data) {
            return [];
        }

        const labels = this.extractLabelsFromVariantData(variant.data);

        return labels.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private extractLabelsFromVariantData(data: VariantData | undefined): Label[] {
        if (!data || !this.legalEntityId) {
            return [];
        }

        const fieldKey = `${PRODUCT_LABELS_DATA_KEY_PREFIX}_${this.legalEntityId}` as const;
        const values = data[fieldKey]?.value?.$values;
        if (!Array.isArray(values)) {
            return [];
        }

        return values.map(label => this.mapLabel(label)).filter((label): label is Label => !!label);
    }

    private buildCategoryPlpUrl(category: RelewiseCategoryResult): string | undefined {
        if (!this.isEnabled() || !category) {
            return;
        }

        const firstPath = category?.paths?.[0]?.pathFromRoot;
        const recordId = category.data?.[`RecordId_${this.legalEntityId}`]?.value;
        const categoryId = category.categoryId;

        if (!firstPath?.length || !recordId || !categoryId) {
            return;
        }

        // Relewise does not fetch the path of the current category,
        // it fetches instead the paths for all its children,
        // so we need to stop at the current category.
        const categoryIndex = firstPath.findIndex(step => step.id === categoryId);
        if (categoryIndex === -1) {
            return;
        }

        const urlPath = firstPath
            .slice(0, categoryIndex + 1)
            .filter(s => !!s.id)
            .map(s => this.normalizeCategoryPathStepForUrl(s.id))
            .join('/');

        return `${this.sitePath}/${urlPath}/${recordId}.c`;
    }

    private normalizeCategoryPathStepForUrl(stepId: string | null | undefined): string | undefined {
        if (!stepId) {
            return;
        }

        const unprefixedId = this.unprefixId(stepId);

        // No need to slugify this since the ID stored in Relewise is already slugified
        return encodeURIComponent(unprefixedId);
    }

    public getCategorySlug(categoryName: string) {
        return (
            categoryName
                ?.toLocaleLowerCase()
                .replace(/&/g, '') // Remove ampersands
                .replace(/ /g, '-') // Replace spaces with dashes
                .replace(/-+/g, '-') ?? '' // Collapse consecutive dashes
        );
    }

    private formatVariantPrices(variant: RelewiseVariantResult | undefined) {
        const basePriceFormatted = formatPrice(variant?.listPrice?.toString(), this.currency, this.locale);
        const finalPriceFormatted = formatPrice(variant?.salesPrice?.toString(), this.currency, this.locale);
        return { basePriceFormatted, finalPriceFormatted };
    }

    private getFormattedMemberPrice(variant: RelewiseVariantResult | undefined, shouldPrefix = false): string | undefined {
        const memberPriceValue = variant?.data?.[`MemberPrice_${this.legalEntityId}`]?.value;
        const hasMemberPrice = memberPriceValue !== undefined && memberPriceValue !== null;
        // Only use member price if its lower than discounted price
        const hasRelevantMemberPrice = hasMemberPrice && memberPriceValue < (variant?.salesPrice ?? Number.POSITIVE_INFINITY);

        if (!hasRelevantMemberPrice) {
            return;
        }

        const memberPriceValueFormatted = formatPrice(memberPriceValue.toString(), this.currency, this.locale);
        const formattedMemberPrice = shouldPrefix
            ? `${this.translateCmsString(TranslationKey.MemberPriceMessage, MEMBER_PRICE_LABEL_FALLBACK_TEXT)?.toUpperCase()} ${memberPriceValueFormatted}`
            : memberPriceValueFormatted;

        return formattedMemberPrice;
    }

    private getVariantDiscount(variant: RelewiseVariantResult | undefined): number | undefined {
        if (!variant?.listPrice || !variant.salesPrice) {
            return;
        }
        const discount = Number((variant.listPrice - variant.salesPrice).toFixed(2));
        return discount > 0 ? discount : undefined;
    }

    private mapPrimarySizeVariant(variantCustomData: VariantData | undefined): PrimarySizeVariant | undefined {
        if (!variantCustomData) {
            return;
        }
        const primarySizeVariant = variantCustomData[`PrimarySizeVariant_${this.legalEntityId}`]?.value;
        return {
            variantRecordId: primarySizeVariant?.data?.RecordId?.value,
            concatenatedId: this.unprefixId(primarySizeVariant?.data?.Id?.value),
            relewiseVariantId: primarySizeVariant?.data?.Id?.value,
            size: primarySizeVariant?.data?.Size?.value
        };
    }

    private mapLabel(label: RelewiseDataObject<RelewiseLabel> | undefined): Label | undefined {
        const isMemberPriceLabel = label?.data?.Type?.value === 'MemberDiscount';
        const text = isMemberPriceLabel
            ? this.translateCmsString(TranslationKey.MemberPriceMessage, MEMBER_PRICE_LABEL_FALLBACK_TEXT)
            : label?.data?.Text?.value;

        if (!text || !label?.data?.Color?.value || !label?.data?.TextColor?.value) {
            return;
        }

        return {
            text,
            color: label?.data?.Color?.value,
            textColor: label?.data?.TextColor?.value,
            type: label?.data?.Type?.value,
            order: label?.data?.Order?.value,
            position: label?.data?.Position?.value
        };
    }
}
